import SpacedTextInput, {
  SpacedAutocompleteInput,
} from "components/common/ui/SpacedTextInput";
import React, {
  ChangeEvent,
  FC,
  memo,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Box, Grid, Tooltip } from "@mui/material";
import styled from "styled-components";
import Typography from "@mui/material/Typography";
import CircleIconButton from "components/common/ui/CircleIconButton";
import CloseIcon from "@mui/icons-material/Close";
import {
  defaultMenuItemDisplay,
  DisplayMenuItem,
  withFoodChanged,
} from "models/menu/menuContentsReducer";
import AdaptiveRowHeader from "components/common/ui/AdaptiveRowHeader";
import {
  getFoodItemOptionMaps,
  getFoodItemOptionName,
} from "utils/food/foodItemOptionDisplayUtils";
import { useAppSelector } from "hooks/useReduxStore";
import foodItemOptionsSelector from "store/selectors/food/foodItemOptionsSelector";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { getMenuOptionByPath } from "utils/menu/menu/menuContentDisplay";
import { isMenuItemMissingFood } from "utils/menu/menu/menuDisplayValidation";
import useMenuOptionActions from "hooks/menu/useMenuOptionActions";
import {
  buildFlatFoodLookup,
  getVariantsOfMain,
  getVariantTypeLabel,
  isFoodVariant,
} from "utils/food/foodVariantUtils";
import { MenuOptionEditBoxProps } from "./MenuSectionEditBox";

const SideItemTitle = styled(Typography)`
  display: inline-block;
  width: fit-content;
  white-space: nowrap;
  font-weight: 400;
  color: #a0aab4;
`;

// Label for the "this row is the main, not a variant" choice in the variant
// dropdown. The recipe itself is already named in the adjacent "Select food"
// dropdown, so the original only needs a neutral label here.
const ORIGINAL_OPTION_LABEL = "Original";

interface MenuItemEditBoxProps extends MenuOptionEditBoxProps {
  label?: string;
}

const MenuItemEditBox: FC<MenuItemEditBoxProps> = ({
  foodItemOptions,
  displayMenuOptionUUIDPath,
  menuContents,
  dispatchMenuContents,
  label,
}) => {
  const [foodItemOptionToIdMap, setFoodItemOptionToIdMap] = useState<
    Record<string, number>
  >({ "": 0 });
  const [idToFoodItemOptionMap, setIdToFoodItemOptionMap] = useState<
    Map<number, string>
  >(new Map([[0, ""]]));
  const [menuItemDisplay, setMenuItemDisplay] = useState<DisplayMenuItem>(
    defaultMenuItemDisplay(),
  );
  const isFoodOptionsLoading = useAppSelector(
    foodItemOptionsSelector.isFoodOptionsLoading,
  );
  const { handleMove, handleRemove, handleUpdate } = useMenuOptionActions(
    menuContents,
    dispatchMenuContents,
    displayMenuOptionUUIDPath,
  );

  // Flat lookup of every loaded food keyed by id (mains + nested variants).
  // Used to resolve a row's food (to learn whether it is a variant and, if so,
  // which main it belongs to) from the food options pool.
  const foodById = useMemo(
    () => buildFlatFoodLookup(foodItemOptions),
    [foodItemOptions],
  );

  /**
   * Use effect to sync the menu item input fields from the canonical menu data.
   */
  useEffect(() => {
    const thisMenuItem = getMenuOptionByPath(
      menuContents,
      displayMenuOptionUUIDPath,
    ) as DisplayMenuItem;
    if (!thisMenuItem) {
      return;
    }
    setMenuItemDisplay(thisMenuItem);
  }, [displayMenuOptionUUIDPath, menuContents]);

  /**
   * Use effect to sync the food item option names. Variants nested under each
   * main are flattened in so id→name lookups also resolve variant rows.
   */
  useEffect(() => {
    const flatFoods = Array.from(buildFlatFoodLookup(foodItemOptions).values());
    const { foodItemOptionNamesToIdsMap, idsToFoodItemOptionNamesMap } =
      getFoodItemOptionMaps(flatFoods);
    setFoodItemOptionToIdMap(foodItemOptionNamesToIdsMap);
    setIdToFoodItemOptionMap(idsToFoodItemOptionNamesMap);
  }, [foodItemOptions]);

  // The "Select food" dropdown shows mains only — a specific variant is chosen
  // through the separate variant dropdown.
  const mainFoodOptionNames = useMemo(() => {
    const mainIds = new Set(
      foodItemOptions.filter((f) => !isFoodVariant(f)).map((f) => f.id),
    );
    return Object.keys(foodItemOptionToIdMap).filter((name) => {
      const id = foodItemOptionToIdMap[name];
      return id === 0 || mainIds.has(id);
    });
  }, [foodItemOptionToIdMap, foodItemOptions]);

  // The main food this row resolves to: the row's own food when it is a main,
  // or its main when the row is a variant. The menu item ships `original_food_id`
  // for variant rows, so this resolves correctly even before the food options
  // pool (which carries the variant list) has loaded; the pool is only a
  // fallback for rows whose `original_food_id` wasn't persisted. Drives both
  // dropdowns — the "Select food" value and the available variant list.
  const mainFoodId = useMemo(() => {
    const foodId = menuItemDisplay.food_id;
    if (!foodId) {
      return 0;
    }
    const originalFoodId = menuItemDisplay.original_food_id;
    if (originalFoodId && originalFoodId !== foodId) {
      return originalFoodId;
    }
    const food = foodById.get(foodId);
    if (food && isFoodVariant(food)) {
      return food.original_food_id ?? foodId;
    }
    return foodId;
  }, [menuItemDisplay.food_id, menuItemDisplay.original_food_id, foodById]);

  // This row is itself a variant when its food is not the resolved main.
  const isVariantItem =
    !!menuItemDisplay.food_id && menuItemDisplay.food_id !== mainFoodId;

  const variantsOfMain = useMemo(
    () => getVariantsOfMain(foodItemOptions, mainFoodId),
    [foodItemOptions, mainFoodId],
  );

  // Label for the row's currently selected variant. Prefers the variant's
  // food-options entry (clean variant-type text); before the pool loads it
  // falls back to the name shipped on the menu item, so a variant row still
  // shows what it is during loading.
  const selectedVariantLabel = useMemo(() => {
    const foodId = menuItemDisplay.food_id;
    if (!foodId || foodId === mainFoodId) {
      return "";
    }
    const food = foodById.get(foodId);
    if (food) {
      return getVariantTypeLabel(food);
    }
    return menuItemDisplay.food_item?.food_name ?? `Variant ${foodId}`;
  }, [
    menuItemDisplay.food_id,
    menuItemDisplay.food_item,
    mainFoodId,
    foodById,
  ]);

  // Show the variant dropdown when the main is known to have variants, or when
  // this row is itself a variant (so it keeps showing during pool loading).
  const showVariantDropdown = variantsOfMain.length > 0 || isVariantItem;

  // Variant dropdown options: the "Original" (main) plus each variant, mapped to
  // the food id each one selects. The current variant is always included so the
  // controlled value matches an option even before the pool's variant list
  // arrives.
  const variantOptionToIdMap = useMemo(() => {
    const map: Record<string, number> = {
      [ORIGINAL_OPTION_LABEL]: mainFoodId,
    };
    variantsOfMain.forEach((variant) => {
      map[getVariantTypeLabel(variant)] = variant.id;
    });
    const foodId = menuItemDisplay.food_id;
    if (foodId && foodId !== mainFoodId && selectedVariantLabel) {
      map[selectedVariantLabel] = foodId;
    }
    return map;
  }, [
    variantsOfMain,
    mainFoodId,
    menuItemDisplay.food_id,
    selectedVariantLabel,
  ]);
  const variantOptionNames = useMemo(
    () => Object.keys(variantOptionToIdMap),
    [variantOptionToIdMap],
  );

  // Value shown in the "Select food" dropdown — always the main's name, or
  // empty when the row has no food (e.g. just cleared). Without the empty guard
  // a cleared row would fall back to its stale `food_item` name, leaving the
  // dropdown displaying a food that `food_id` no longer points at — re-picking
  // that same food would then be a no-op (no onChange) and the variant dropdown
  // would never reappear.
  const mainFoodDisplayValue = useMemo(() => {
    if (!mainFoodId) {
      return "";
    }
    const fromOptions = idToFoodItemOptionMap.get(mainFoodId);
    if (fromOptions) {
      return fromOptions;
    }
    // Before food options finish loading, fall back to the row's inline name.
    return menuItemDisplay.food_item
      ? getFoodItemOptionName(menuItemDisplay.food_item)
      : "";
  }, [idToFoodItemOptionMap, mainFoodId, menuItemDisplay.food_item]);

  // Value shown in the variant dropdown — "Original" when the row holds the
  // main, otherwise the selected variant's label (which itself falls back to
  // the shipped name during pool loading).
  const variantDisplayValue = selectedVariantLabel || ORIGINAL_OPTION_LABEL;

  /**
   * Commit the row's local state to the canonical menu data on blur.
   */
  const handleUpdateMenuItem = useCallback(() => {
    handleUpdate(menuItemDisplay);
  }, [handleUpdate, menuItemDisplay]);

  /**
   * Handle "Select food" (main) changes. Selecting a main makes the row that
   * main and clears any variant linkage; the variant dropdown resets to
   * Original because `food_id` now equals the new main.
   */
  const handleMainFoodChange = useCallback(
    (_: SyntheticEvent, newValue: string | null) => {
      const newFoodId =
        newValue !== null ? (foodItemOptionToIdMap[newValue] ?? 0) : 0;
      setMenuItemDisplay(
        withFoodChanged({
          item: menuItemDisplay,
          foodId: newFoodId,
          originalFoodId: null,
        }),
      );
    },
    [foodItemOptionToIdMap, menuItemDisplay],
  );

  /**
   * Handle variant dropdown changes. Picking the original points the row at the
   * main (no variant linkage); picking a variant points it at that variant and
   * records `original_food_id` so the saved menu keeps the variant-of signal.
   */
  const handleVariantChange = useCallback(
    (_: SyntheticEvent, newValue: string | null) => {
      const chosenId =
        newValue !== null
          ? (variantOptionToIdMap[newValue] ?? mainFoodId)
          : mainFoodId;
      if (chosenId === mainFoodId) {
        setMenuItemDisplay(
          withFoodChanged({
            item: menuItemDisplay,
            foodId: mainFoodId,
            originalFoodId: null,
          }),
        );
        return;
      }
      const variantFood = foodById.get(chosenId);
      setMenuItemDisplay(
        withFoodChanged({
          item: menuItemDisplay,
          foodId: chosenId,
          originalFoodId: variantFood?.original_food_id ?? mainFoodId,
        }),
      );
    },
    [variantOptionToIdMap, mainFoodId, foodById, menuItemDisplay],
  );

  /**
   * Handle menu item text input fields changes
   */
  const handleMenuItemInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const keyInput: string = event.target.name;
      let valueInput: number | string = event.target.value;
      if (!valueInput.endsWith(".") && keyInput !== "display_name") {
        valueInput = Number(valueInput) || valueInput;
      }
      setMenuItemDisplay({
        ...menuItemDisplay,
        [event.target.name]: valueInput,
      });
    },
    [menuItemDisplay],
  );

  return (
    <Box display="flex" justifyContent="flex-end">
      {label ? (
        <AdaptiveRowHeader switchWidthInPixel={900}>
          <Tooltip title="Remove this side">
            <CircleIconButton
              positive
              danger
              sizepx={30}
              onClick={handleRemove}
            >
              <CloseIcon />
            </CircleIconButton>
          </Tooltip>
          <Box padding={1}>
            <SideItemTitle display="inline-block">{label}</SideItemTitle>
          </Box>
          <Tooltip title="Move this side up">
            <CircleIconButton sizepx={30} onClick={handleMove("up")}>
              <KeyboardArrowUpIcon />
            </CircleIconButton>
          </Tooltip>
          <Tooltip title="Move this side down">
            <CircleIconButton sizepx={30} onClick={handleMove("down")}>
              <KeyboardArrowDownIcon />
            </CircleIconButton>
          </Tooltip>
        </AdaptiveRowHeader>
      ) : null}
      <Grid container spacing={1} onBlur={handleUpdateMenuItem}>
        <Grid item xs={12} md={showVariantDropdown ? 4 : 5}>
          <SpacedAutocompleteInput
            loading={isFoodOptionsLoading}
            options={mainFoodOptionNames}
            label="Select food"
            value={mainFoodDisplayValue}
            onChange={handleMainFoodChange}
            error={isMenuItemMissingFood(menuItemDisplay)}
            shrink={isMenuItemMissingFood(menuItemDisplay) || undefined}
          />
        </Grid>
        {showVariantDropdown ? (
          <Grid item xs={12} md={2}>
            <SpacedAutocompleteInput
              loading={isFoodOptionsLoading}
              options={variantOptionNames}
              label="Variant"
              value={variantDisplayValue}
              onChange={handleVariantChange}
            />
          </Grid>
        ) : null}
        <Grid item xs={12} md={showVariantDropdown ? 4 : 5}>
          <SpacedTextInput
            type="text"
            label="Display name"
            name="display_name"
            value={menuItemDisplay.display_name ?? ""}
            onChange={handleMenuItemInputChange}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <SpacedTextInput
            type="number"
            label="Production"
            name="cap"
            value={menuItemDisplay.cap ?? ""}
            onChange={handleMenuItemInputChange}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
export default memo(MenuItemEditBox);
